import { Coordinate } from '@components/Map/Map';
import { ToastType } from '@components/Toast/Toast';
import {
  fetchCoordinatesFromAddress,
  fetchCoordinatesFromZip,
  fetchLoadTender,
  fetchPastStops,
  fetchTopStops,
  getNearbyCarriers,
  postLoadStatus,
  setRateConRecieved,
} from '@store/orders/services/orderLoadsServices';
import {
  setLoadTenderStore,
  setMapCords,
  setOrderStore,
  setStops,
} from '@store/orders/store';
import {
  ILatLon,
  loadStatus,
  NearbyCarrierRequestViewModel,
  TabState,
} from '@store/orders/types';
import { setLoadBoardStore } from '@store/loadboard/store';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { logError } from '@utils/errorLogging';
import { handleToast } from '@utils/utils';
import { ICityState, IZip } from '@views/order/components';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { produce } from 'solid-js/store';

import { setActiveCarrierTab } from './carrierActions';

export const toggleRateCon = async (loadId: number, recieved: boolean) => {
  try {
    setLoadBoardStore('overlay', true);
    const apiResonse = await setRateConRecieved(loadId, recieved);
    if (Boolean(apiResonse)) {
      setLoadBoardStore(
        'items',
        (items) => items.loadId === loadId,
        'confirmationSigned',
        recieved,
      );
    }
    setLoadBoardStore('overlay', false);
  } catch (error) {
    setLoadBoardStore('overlay', false);
  }
};

export const changeLoadStatus = async (
  loadId: number,
  status: string,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .orderDetails.loadDetailsStatus,
): Promise<boolean> => {
  const loadStatus: loadStatus = {
    id: loadId,
    status: status,
  };
  try {
    setLoadBoardStore('overlay', true);
    const succeeded = await postLoadStatus(loadStatus, changeTrackingSource);
    if (!succeeded) {
      setLoadBoardStore('overlay', false);
      return false;
    }
    setLoadBoardStore(
      'items',
      (items) => items.loadId === loadId,
      'status',
      status,
    );
    setOrderStore(
      'order',
      'loads',
      (load) => load.id === loadId,
      'status',
      status,
    );
    setLoadBoardStore('overlay', false);
    return true;
  } catch (error) {
    handleToast(ToastType.Error, (error as Error).message);
    setLoadBoardStore('overlay', false);
    return false;
  }
};

export const fetchStopsForStopsModal = async (customerId: number) => {
  const [pastStops, topStops] = await Promise.all([
    fetchPastStops(customerId),
    fetchTopStops(customerId),
  ]);
  setStops(topStops, pastStops);
};

export const addNewLoadToOrder = (load: ILoadEditViewModel) => {
  setOrderStore(
    produce((draft) => {
      draft.order.loads.push(load);
    }),
  );
};

export const removeLoadFromOrder = (loadIndex: number) => {
  setOrderStore(
    'order',
    'loads',
    produce((loads) => {
      loads.splice(loadIndex, 1);
    }),
  );
  setActiveCarrierTab({ index: 0, type: 'load' } as TabState);
};

export const getStopCoordinates = async (addressArray: IZip[]) => {
  try {
    const response = await fetchCoordinatesFromZip(addressArray);
    const latlngArray: Coordinate[] = [];
    response.forEach((geoElement: ILatLon) => {
      latlngArray.push({
        lat: Number(geoElement.Coords.Lat),
        lng: Number(geoElement.Coords.Lon),
        city: '',
        state: '',
        locationTime: '',
        trackingSource: undefined,
      });
    });
    //update store
    setMapCords(latlngArray);
  } catch (error) {
    handleToast(ToastType.Error, (error as Error).message);
    logError('getStopCoordinates', error as Error);
  }
};

export const getCoordinates = async (addressArray: ICityState[]) => {
  try {
    const response = await fetchCoordinatesFromAddress(addressArray);
    const latlngArray: Coordinate[] = [];
    response.forEach((geoElement: ILatLon) => {
      latlngArray.push({
        lat: Number(geoElement.Coords.Lat),
        lng: Number(geoElement.Coords.Lon),
        city: '',
        state: '',
        locationTime: '',
        trackingSource: undefined,
      });
    });
    return latlngArray;
  } catch (error) {
    handleToast(ToastType.Error, (error as Error).message);
    logError('getCoordinates', error as Error);
    return [];
  }
};

export const fetchLoadTenderDetails = async (orderId: number | string) => {
  try {
    const load = await fetchLoadTender(orderId);
    setLoadTenderStore('loadTender', load);
  } catch (error) {
    setLoadTenderStore(() => ({
      isError: true,
    }));
    setLoadTenderStore('isError', true);
    logError('fetchLoadTenderDetails', error as Error);
    handleToast(ToastType.Error, (error as Error).message);
  } finally {
    setLoadTenderStore('loading', false);
  }
};

export const fetchNearbyCarriers = async (
  payload: NearbyCarrierRequestViewModel,
) => {
  try {
    const response = await getNearbyCarriers(payload);
    if (Array.isArray(response) && response.length > 0) {
      setOrderStore('nearbyCarriers', response);
    }
  } catch (error) {
    handleToast(ToastType.Error, (error as Error).message);
  }
};
